import { Box } from '@mui/material';
import React from 'react';
import { midiToName } from '../util/note';
import type {
  SightReadingLiveCandidate,
  SightReadingLiveFrame,
  SightReadingLiveJudgment,
} from '../testing/sightReadingLiveDiagnostics';

const WAVEFORM_WIDTH = 286;
const WAVEFORM_HEIGHT = 30;

const formatMidiNote = (midiNote: number) => `${midiToName(midiNote)} (${midiNote})`;

const formatMidiNotes = (midiNotes: number[]) =>
  midiNotes.length > 0 ? midiNotes.map(formatMidiNote).join(' + ') : 'none';

const formatCandidates = (candidates: SightReadingLiveCandidate[]) =>
  candidates.length > 0
    ? candidates
        .map(
          (candidate) =>
            `${midiToName(candidate.midiNote)} ${Math.round(candidate.confidence * 100)}%`,
        )
        .join(' · ')
    : 'none';

const judgmentPresentation = (judgment: SightReadingLiveJudgment) => {
  if (judgment.phase === 'offset') {
    if (judgment.state === 'EARLY_OFFSET') return { label: 'RELEASED EARLY', color: '#FFD27D' };
    if (judgment.state === 'LATE_OFFSET') return { label: 'RELEASED LATE', color: '#FFD27D' };
    if (judgment.state === 'NEVER_PLAYED') return { label: 'NO RELEASE', color: '#FF8B8B' };
    return { label: 'RELEASE', color: '#8DCBFF' };
  }

  if (judgment.state === 'HIT') return { label: 'RIGHT', color: '#85E3A5' };
  if (judgment.state === 'NEVER_PLAYED') return { label: 'MISSED', color: '#FF8B8B' };
  if (judgment.state === 'WRONG_NOTE') return { label: 'WRONG NOTE', color: '#FF8B8B' };
  if (judgment.state === 'ADDITIONAL_NOTE') return { label: 'EXTRA NOTE', color: '#FFD27D' };
  if (judgment.state === 'EARLY_ONSET') return { label: 'EARLY', color: '#FFD27D' };
  if (judgment.state === 'LATE_ONSET') return { label: 'LATE', color: '#FFD27D' };
  return { label: judgment.state.replaceAll('_', ' '), color: '#8DCBFF' };
};

const waveformPath = (waveform: number[]) => {
  if (waveform.length === 0) return '';
  const centerY = WAVEFORM_HEIGHT / 2;
  const xScale = waveform.length <= 1 ? 0 : WAVEFORM_WIDTH / (waveform.length - 1);
  return waveform
    .map((sample, index) => {
      const x = index * xScale;
      const y = centerY - Math.max(-1, Math.min(1, sample)) * (centerY - 2);
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
};

const labelSx = {
  color: 'rgba(234, 242, 255, 0.54)',
  fontSize: '9px',
  fontWeight: 800,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
} as const;

const valueSx = {
  marginTop: '2px',
  color: '#F1F6FF',
  fontSize: '10px',
  fontWeight: 700,
  lineHeight: 1.3,
} as const;

const SightReadingLiveReadout = ({
  frame,
  activeBridgeMidiNotes,
  judgments,
}: {
  frame: SightReadingLiveFrame | null;
  activeBridgeMidiNotes: number[];
  judgments: SightReadingLiveJudgment[];
}) => {
  const aboveGate = frame != null && frame.minRms > 0 && frame.rms >= frame.minRms;
  const path = waveformPath(frame?.waveform ?? []);

  return (
    <Box
      data-sight-reading-live-readout
      aria-live="polite"
      sx={{
        marginTop: '9px',
        paddingTop: '9px',
        borderTop: '1px solid rgba(234, 242, 255, 0.12)',
      }}
    >
      <Box sx={{ fontSize: '10px', fontWeight: 900, letterSpacing: '0.02em' }}>
        Live recognition
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '7px 10px',
          marginTop: '7px',
        }}
      >
        <Box>
          <Box sx={labelSx}>Expected bucket</Box>
          <Box data-live-expected-notes sx={valueSx}>
            {formatMidiNotes(frame?.expectedMidiNotes ?? [])}
          </Box>
        </Box>
        <Box>
          <Box sx={labelSx}>Recognizer bucket</Box>
          <Box data-live-recognized-notes sx={valueSx}>
            {formatMidiNotes(frame?.recognizedMidiNotes ?? [])}
          </Box>
        </Box>
        <Box>
          <Box sx={labelSx}>Candidates</Box>
          <Box data-live-candidate-notes sx={valueSx}>
            {formatCandidates(frame?.candidates ?? [])}
          </Box>
        </Box>
        <Box>
          <Box sx={labelSx}>Game input</Box>
          <Box data-live-game-input sx={valueSx}>
            {formatMidiNotes(activeBridgeMidiNotes)}
          </Box>
        </Box>
      </Box>

      <Box sx={{ marginTop: '8px' }}>
        <Box sx={labelSx}>
          Signal {aboveGate ? 'above gate' : 'below gate'} · RMS {(frame?.rms ?? 0).toFixed(4)} /
          {(frame?.minRms ?? 0).toFixed(4)}
        </Box>
        <Box
          component="svg"
          data-live-waveform
          viewBox={`0 0 ${WAVEFORM_WIDTH} ${WAVEFORM_HEIGHT}`}
          preserveAspectRatio="none"
          aria-label="Latest downsampled microphone waveform"
          sx={{
            display: 'block',
            width: '100%',
            height: `${WAVEFORM_HEIGHT}px`,
            marginTop: '4px',
            borderRadius: '5px',
            backgroundColor: 'rgba(121, 216, 255, 0.05)',
          }}
        >
          <path
            d={`M 0 ${WAVEFORM_HEIGHT / 2} L ${WAVEFORM_WIDTH} ${WAVEFORM_HEIGHT / 2}`}
            stroke="rgba(234, 242, 255, 0.12)"
            strokeWidth="1"
          />
          {path && <path d={path} fill="none" stroke="#79D8FF" strokeWidth="1.35" />}
        </Box>
      </Box>

      <Box sx={{ marginTop: '8px' }}>
        <Box sx={labelSx}>Recent judgments</Box>
        <Box data-live-judgments sx={{ display: 'grid', gap: '5px', marginTop: '5px' }}>
          {judgments.length === 0 && (
            <Box sx={{ color: 'rgba(234, 242, 255, 0.58)', fontSize: '10px' }}>
              Waiting for the exercise to grade a note.
            </Box>
          )}
          {judgments.map((judgment) => {
            const presentation = judgmentPresentation(judgment);
            const heardMidiNotes =
              judgment.phase === 'onset' && judgment.playedMidiNote != null
                ? [judgment.playedMidiNote]
                : [];
            return (
              <Box
                key={judgment.id}
                data-live-judgment={judgment.state}
                sx={{
                  padding: '6px 7px',
                  borderRadius: '7px',
                  backgroundColor: 'rgba(234, 242, 255, 0.055)',
                  fontSize: '9px',
                  lineHeight: 1.35,
                }}
              >
                <Box sx={{ color: presentation.color, fontWeight: 900 }}>
                  {presentation.label} · expected {formatMidiNote(judgment.midiNote)}
                </Box>
                {judgment.phase === 'onset' && (
                  <Box sx={{ marginTop: '2px', color: 'rgba(234, 242, 255, 0.75)' }}>
                    Grader heard {formatMidiNotes(heardMidiNotes)} · recognizer bucket{' '}
                    {formatMidiNotes(judgment.recognizedMidiNotes)} · candidates{' '}
                    {formatMidiNotes(judgment.candidateMidiNotes)}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default SightReadingLiveReadout;
